import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { exercisesApi, usersApi, clientsApi, subCompaniesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { TIMEZONES, getRelativeOffset } from '../../lib/timezone';
import { TimezoneBadge } from '../../components/shared/TimezoneIndicator';
import { 
  Plus, 
  Search, 
  Filter, 
  MoreVertical,
  Target,
  CalendarIcon,
  PlayCircle,
  PauseCircle,
  CheckCircle2,
  Clock,
  Users,
  Trash2,
  Edit,
  Eye,
  Copy,
  RotateCcw
} from 'lucide-react';

const EXERCISE_TYPES = [
  { value: 'tabletop', label: 'Tabletop Exercise' },
  { value: 'functional', label: 'Functional Exercise' },
  { value: 'full_scale', label: 'Full-Scale Exercise' },
  { value: 'custom', label: 'Custom Exercise' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'planned', label: 'Planned' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'disabled', label: 'Disabled' },
];

const PARTICIPANT_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
];

export default function ExercisesPage() {
  const { isController, isParticipant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exercises, setExercises] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]); // for filters
  const [formSubCompanies, setFormSubCompanies] = useState([]); // for create form
  const [clientFilter, setClientFilter] = useState('all');
  const [subCompanyFilter, setSubCompanyFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    exercise_type: 'tabletop',
    scenario_summary: '',
    start_date: null,
    end_date: null,
    timezone: 'UTC',
    assigned_users: [],
    client_id: '',
    sub_company_id: ''
  });

  // Duplicate edit state
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatedExercise, setDuplicatedExercise] = useState(null);
  const [dupFormData, setDupFormData] = useState({ name: '', client_id: '', sub_company_id: '' });
  const [dupSubCompanies, setDupSubCompanies] = useState([]);
  const [dupLoading, setDupLoading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [statusFilter, clientFilter, subCompanyFilter]);

  useEffect(() => {
    if (statusFilter !== 'all') {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, setSearchParams]);

  const loadData = async () => {
    try {
      const [clientsData, exercisesData, usersData] = await Promise.all([
        clientsApi.getAll().catch(() => []),
        exercisesApi.getAll(
          statusFilter === 'all' ? null : statusFilter,
          clientFilter === 'all' ? null : clientFilter,
          subCompanyFilter === 'all' ? null : subCompanyFilter
        ),
        isParticipant() ? Promise.resolve([]) : usersApi.getAll().catch(() => [])
      ]);
      setClients(clientsData);
      setExercises(exercisesData);
      setUsers(usersData);

      if (clientFilter !== 'all') {
        const subs = await subCompaniesApi.getAll(clientFilter).catch(() => []);
        setSubCompanies(subs);
      } else {
        setSubCompanies([]);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load exercises');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExercise = async (e) => {
    e.preventDefault();

    // Validate exercise name — only allow letters, numbers, spaces, hyphens, and underscores
    const nameRegex = /^[a-zA-Z0-9\s\-_]+$/;
    if (!formData.name.trim()) {
      toast.error('Exercise name is required');
      return;
    }
    if (!nameRegex.test(formData.name)) {
      toast.error('Exercise name can only contain letters, numbers, spaces, hyphens, and underscores');
      return;
    }

    if (!formData.client_id) {
      toast.error('Please select a client company');
      return;
    }

    setFormLoading(true);

    try {
      const payload = {
        ...formData,
        client_id: formData.client_id || null,
        sub_company_id: formData.sub_company_id ? formData.sub_company_id : null,
      };
      if (editingExerciseId) {
        await exercisesApi.update(editingExerciseId, payload);
        toast.success('Exercise updated successfully');
      } else {
        await exercisesApi.create(payload);
        toast.success('Exercise created successfully');
      }
      setCreateDialogOpen(false);
      setEditingExerciseId(null);
      setFormData({
        name: '',
        description: '',
        exercise_type: 'tabletop',
        scenario_summary: '',
        start_date: null,
        end_date: null,
        timezone: 'UTC',
        assigned_users: [],
        client_id: '',
        sub_company_id: ''
      });
      setFormSubCompanies([]);
      loadData();
    } catch (error) {
      toast.error(error.message || (editingExerciseId ? 'Failed to update exercise' : 'Failed to create exercise'));
    } finally {
      setFormLoading(false);
    }
  };

  // V14.10.6 — Open the same dialog in edit mode, prefilled with the
  // existing exercise's fields (name, description, type, timezone, dates,
  // client, sub-company, scenario_summary).
  const openEditExercise = async (exercise) => {
    setEditingExerciseId(exercise.exercise_id);
    setFormData({
      name: exercise.name || '',
      description: exercise.description || '',
      exercise_type: exercise.exercise_type || 'tabletop',
      scenario_summary: exercise.scenario_summary || '',
      start_date: exercise.start_date ? new Date(exercise.start_date) : null,
      end_date: exercise.end_date ? new Date(exercise.end_date) : null,
      timezone: exercise.timezone || 'UTC',
      assigned_users: exercise.assigned_users || [],
      client_id: exercise.client_id || '',
      sub_company_id: exercise.sub_company_id || '',
    });
    if (exercise.client_id) {
      const subs = await subCompaniesApi.getAll(exercise.client_id).catch(() => []);
      setFormSubCompanies(subs);
    } else {
      setFormSubCompanies([]);
    }
    setCreateDialogOpen(true);
  };

  const handleDeleteExercise = async (exerciseId) => {
    if (!window.confirm('Are you sure you want to delete this exercise?')) return;
    
    try {
      await exercisesApi.delete(exerciseId);
      toast.success('Exercise deleted');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete exercise');
    }
  };

  const handleDuplicateExercise = async (exercise) => {
    try {
      const newExercise = await exercisesApi.duplicate(exercise.exercise_id);
      toast.success('Exercise duplicated');
      setDuplicatedExercise(newExercise);
      setDupFormData({
        name: newExercise.name,
        client_id: newExercise.client_id || '',
        sub_company_id: newExercise.sub_company_id || ''
      });
      // Load sub-companies for the current client
      if (newExercise.client_id) {
        const subs = await subCompaniesApi.getAll(newExercise.client_id).catch(() => []);
        setDupSubCompanies(subs);
      } else {
        setDupSubCompanies([]);
      }
      setDuplicateDialogOpen(true);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to duplicate exercise');
    }
  };

  const handleSaveDuplicate = async () => {
    if (!duplicatedExercise) return;
    setDupLoading(true);
    try {
      await exercisesApi.update(duplicatedExercise.exercise_id, {
        name: dupFormData.name,
        client_id: dupFormData.client_id || null,
        sub_company_id: dupFormData.sub_company_id || null,
      });
      toast.success('Duplicated exercise updated');
      setDuplicateDialogOpen(false);
      setDuplicatedExercise(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update duplicated exercise');
    } finally {
      setDupLoading(false);
    }
  };

  const handleResetExercise = async (exercise) => {
    if (!window.confirm(`Reset "${exercise.name}" to draft? This will clear all responses, timeline events, activity logs, and reset inject statuses.`)) return;

    try {
      const result = await exercisesApi.reset(exercise.exercise_id);
      toast.success(`Exercise reset. Cleared ${result.responses_cleared} responses, ${result.timeline_events_cleared} timeline events, ${result.activity_logs_cleared} activity logs.`);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to reset exercise');
    }
  };

  const filteredExercises = exercises.filter(ex => {
    const matchesSearch = ex.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ex.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ex.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <PlayCircle className="w-4 h-4 text-green-500" />;
      case 'planned': return <PauseCircle className="w-4 h-4 text-yellow-500" />;
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-purple-500" />;
      case 'disabled': return <Clock className="w-4 h-4 text-red-500" />;
      default: return <Target className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      active: 'status-live',
      planned: 'status-paused',
      completed: 'status-completed',
      disabled: 'status-draft',
      draft: 'status-draft'
    };
    return classes[status] || 'status-draft';
  };

  return (
    <DashboardLayout title="Exercises">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 flex-col sm:flex-row flex-wrap gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search exercises..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-exercises"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[160px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                {(isParticipant() ? PARTICIPANT_STATUS_FILTERS : STATUS_FILTERS).map(filter => (
                  <SelectItem key={filter.value} value={filter.value}>
                    {filter.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {!isParticipant() && (
              <>
                <Select value={clientFilter} onValueChange={(v) => { setClientFilter(v); setSubCompanyFilter('all'); }}>
                  <SelectTrigger className="w-full sm:w-[220px]" data-testid="client-filter">
                    <SelectValue placeholder="Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.client_id} value={c.client_id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={subCompanyFilter}
                  onValueChange={setSubCompanyFilter}
                  disabled={clientFilter === 'all' || subCompanies.length === 0}
                >
                  <SelectTrigger className="w-full sm:w-[240px]" data-testid="sub-company-filter">
                    <SelectValue placeholder={subCompanies.length ? 'Sub-Company' : 'No sub-companies'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sub-Companies</SelectItem>
                    {subCompanies.map((s) => (
                      <SelectItem key={s.sub_company_id} value={s.sub_company_id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}

          </div>
          
          {isController() && (
            <Dialog open={createDialogOpen} onOpenChange={(v) => {
              setCreateDialogOpen(v);
              if (!v) {
                setEditingExerciseId(null);
                setFormData({
                  name: '', description: '', exercise_type: 'tabletop',
                  scenario_summary: '', start_date: null, end_date: null,
                  timezone: 'UTC', assigned_users: [], client_id: '', sub_company_id: '',
                });
                setFormSubCompanies([]);
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="create-exercise-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Exercise
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-secondary text-xl">{editingExerciseId ? 'Edit Exercise' : 'Create New Exercise'}</DialogTitle>
                  <DialogDescription>
                    Set up a new exercise with scenarios and participants
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleCreateExercise} className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Exercise Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Q4 Emergency Response Drill"
                        required
                        data-testid="exercise-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="type">Exercise Type</Label>
                      <Select 
                        value={formData.exercise_type} 
                        onValueChange={(v) => setFormData({...formData, exercise_type: v})}
                      >
                        <SelectTrigger data-testid="exercise-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {EXERCISE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Client Company *</Label>
                      <Select
                        value={formData.client_id}
                        onValueChange={async (v) => {
                          setFormData({ ...formData, client_id: v, sub_company_id: '' });
                          if (v) {
                            const subs = await subCompaniesApi.getAll(v).catch(() => []);
                            setFormSubCompanies(subs);
                          } else {
                            setFormSubCompanies([]);
                          }
                        }}
                      >
                        <SelectTrigger data-testid="exercise-client-select">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((c) => (
                            <SelectItem key={c.client_id} value={c.client_id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Sub-Company (optional)</Label>
                      <Select
                        value={formData.sub_company_id || 'none'}
                        onValueChange={(v) => setFormData({ ...formData, sub_company_id: v === 'none' ? '' : v })}
                        disabled={!formData.client_id || formSubCompanies.length === 0}
                      >
                        <SelectTrigger data-testid="exercise-sub-company-select">
                          <SelectValue placeholder={formSubCompanies.length ? 'Select sub-company' : 'No sub-companies'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Main client (no sub-company)</SelectItem>
                          {formSubCompanies.map((s) => (
                            <SelectItem key={s.sub_company_id} value={s.sub_company_id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Brief description of the exercise..."
                      rows={2}
                      data-testid="exercise-description-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="scenario">Scenario Summary</Label>
                    <Textarea
                      id="scenario"
                      value={formData.scenario_summary}
                      onChange={(e) => setFormData({...formData, scenario_summary: e.target.value})}
                      placeholder="Describe the scenario participants will face..."
                      rows={3}
                      data-testid="exercise-scenario-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.start_date ? format(formData.start_date, 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.start_date}
                            onSelect={(date) => setFormData({...formData, start_date: date})}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.end_date ? format(formData.end_date, 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={formData.end_date}
                            onSelect={(date) => setFormData({...formData, end_date: date})}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  {/* Timezone Selection */}
                  <div className="space-y-2">
                    <Label>Exercise Timezone</Label>
                    <Select 
                      value={formData.timezone} 
                      onValueChange={(value) => setFormData({...formData, timezone: value})}
                    >
                      <SelectTrigger data-testid="timezone-select">
                        <Clock className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map(tz => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.timezone && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        {getRelativeOffset(formData.timezone)}
                      </p>
                    )}
                  </div>
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={formLoading} data-testid="submit-exercise-btn">
                      {formLoading ? (editingExerciseId ? 'Saving...' : 'Creating...') : (editingExerciseId ? 'Save Changes' : 'Create Exercise')}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Exercises Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredExercises.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Target className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No exercises found</p>
              <p className="text-muted-foreground mb-4">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Get started by creating your first exercise'}
              </p>
              {isController() && !searchQuery && statusFilter === 'all' && (
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Exercise
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exercises-grid">
            {filteredExercises.map((exercise) => (
              <Card 
                key={exercise.exercise_id} 
                className="group hover:border-primary/30 transition-all"
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(exercise.status)}
                      <Badge 
                        variant="outline" 
                        className={`${getStatusBadgeClass(exercise.status)} border capitalize text-xs`}
                      >
                        {exercise.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/exercises/${exercise.exercise_id}`}>
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Link>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/exercises/${exercise.exercise_id}`} aria-label="View Details">
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                            </Link>
                          </DropdownMenuItem>
                        {isController() && exercise.status === 'active' && (
                          <DropdownMenuItem asChild>
                            <Link to={`/control-room/${exercise.exercise_id}`}>
                              <PlayCircle className="w-4 h-4 mr-2" />
                              Control Room
                            </Link>
                          </DropdownMenuItem>
                        )}
                        {isController() && (
                          <DropdownMenuItem
                            onClick={() => openEditExercise(exercise)}
                            data-testid={`edit-exercise-${exercise.exercise_id}`}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                        )}
                        {isController() && (
                          <DropdownMenuItem
                            onClick={() => handleDuplicateExercise(exercise)}
                            data-testid={`duplicate-exercise-${exercise.exercise_id}`}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                        )}
                        {isController() && (
                          <DropdownMenuItem
                            onClick={() => handleResetExercise(exercise)}
                            data-testid={`reset-exercise-${exercise.exercise_id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Reset Exercise
                          </DropdownMenuItem>
                        )}
                        {isController() && (
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => handleDeleteExercise(exercise.exercise_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <CardTitle className="text-lg mt-2 group-hover:text-primary transition-colors">
                    <Link to={`/exercises/${exercise.exercise_id}`}>
                      {exercise.name}
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {exercise.description || 'No description provided'}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      <span className="capitalize">{exercise.exercise_type}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span>{exercise.assigned_users?.length || 0} assigned</span>
                    </div>
                  </div>
                  
                  {exercise.start_date && (
                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-3 h-3" />
                        <span>
                          {new Date(exercise.start_date).toLocaleDateString()}
                          {exercise.end_date && ` - ${new Date(exercise.end_date).toLocaleDateString()}`}
                        </span>
                      </div>
                      {exercise.timezone && (
                        <TimezoneBadge timezone={exercise.timezone} />
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Duplicate Edit Dialog */}
        <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-secondary text-xl">Edit Duplicated Exercise</DialogTitle>
              <DialogDescription>
                Update the name and reassign the duplicated exercise to a different client or sub-company.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Exercise Name</Label>
                <Input
                  value={dupFormData.name}
                  onChange={(e) => setDupFormData({ ...dupFormData, name: e.target.value })}
                  data-testid="duplicate-exercise-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Company</Label>
                <Select
                  value={dupFormData.client_id || 'none'}
                  onValueChange={async (v) => {
                    const cid = v === 'none' ? '' : v;
                    setDupFormData({ ...dupFormData, client_id: cid, sub_company_id: '' });
                    if (cid) {
                      const subs = await subCompaniesApi.getAll(cid).catch(() => []);
                      setDupSubCompanies(subs);
                    } else {
                      setDupSubCompanies([]);
                    }
                  }}
                >
                  <SelectTrigger data-testid="duplicate-client-select">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No client</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.client_id} value={c.client_id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Sub-Company (optional)</Label>
                <Select
                  value={dupFormData.sub_company_id || 'none'}
                  onValueChange={(v) => setDupFormData({ ...dupFormData, sub_company_id: v === 'none' ? '' : v })}
                  disabled={!dupFormData.client_id || dupSubCompanies.length === 0}
                >
                  <SelectTrigger data-testid="duplicate-sub-company-select">
                    <SelectValue placeholder={dupSubCompanies.length ? 'Select sub-company' : 'No sub-companies'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Main client (no sub-company)</SelectItem>
                    {dupSubCompanies.map((s) => (
                      <SelectItem key={s.sub_company_id} value={s.sub_company_id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
                Skip
              </Button>
              <Button onClick={handleSaveDuplicate} disabled={dupLoading} data-testid="save-duplicate-btn">
                {dupLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
